import React, { useState } from "react";

//UI Material
import { Avatar, Button, Card, Divider, Grid, Stack } from "@mui/material";

//Material Icons
import { Edit } from "@mui/icons-material";

//Helpers
import {
  typographyForCustomizedFontSize,
  typographyForMainTitles,
  typographyForSubtitles,
  typographyForText,
} from "../../Helpers/typography";

//Local components
import SerialNumberDrawer from "../drawers/SerialNumberDrawer";
import ComModDrawer from "../drawers/ComModDrawer";
import ContainedButton from "../basics/ContainedButton";
import SpecsDrawer from "../drawers/SpecsDrawer";
import { selectProductImage } from "../../Helpers/productSelector";

export default function ProducedItemSpecifications({
  product,
  annotations,
  specifications,
  getProduct,
}) {
  // ==========> Component States<==========
  //State for open/close serial drawer
  const [serialDrawerOpen, setSerialDrawerOpen] = useState(false);
  //State for open/close comMod drawer
  const [comModDrawerOpen, setComModDrawerOpen] = useState(false);
  //State for open/close specs drawer
  const [openSpecsDrawer, setOpenSpecsDrawer] = useState(false);
  //User data from localStorage
  const userData = JSON.parse(localStorage.getItem("userData"));

  const productAditionalData = product.aditionalData;

  // ==========>Component<==========
  return (
    <Card raised sx={{ height: "100%" }}>
      <SpecsDrawer
        open={openSpecsDrawer}
        setOpen={setOpenSpecsDrawer}
        product={product}
        getData={getProduct}
      />
      <SerialNumberDrawer
        open={serialDrawerOpen}
        setOpen={setSerialDrawerOpen}
        product={product}
        getData={getProduct}
      />
      <ComModDrawer
        open={comModDrawerOpen}
        setOpen={setComModDrawerOpen}
        product={product}
        getData={getProduct}
      />
      <Grid container sx={{ padding: "15px" }} spacing={1}>
        <Grid item xs={12}>
          {typographyForSubtitles("Especificaciones", "secondary")}
        </Grid>
        <Grid item xs={12}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            textAlign="left"
          >
            <Avatar
              src={selectProductImage(productAditionalData.productName)}
              alt="product-img"
              sx={{ height: "100px", width: "100px" }}
            />
            {typographyForMainTitles(productAditionalData.productName)}
          </Stack>
        </Grid>
        <Grid item xs={productAditionalData.comMod ? 6 : 12}>
          <Stack alignItems="center">
            {typographyForCustomizedFontSize("Serial", "11px", null, "bold")}
            {productAditionalData.serial === "Sin asignar" ? (
              <Button
                size="small"
                onClick={() => {
                  setSerialDrawerOpen(true);
                }}
                variant="outlined"
                endIcon={<Edit />}
              >
                EDITAR
              </Button>
            ) : (
              typographyForText(productAditionalData.serial)
            )}
          </Stack>
        </Grid>
        {productAditionalData.comMod && (
          <Grid item xs={6}>
            <Stack alignItems="center">
              {typographyForCustomizedFontSize("ComMod", "11px", null, "bold")}
              {productAditionalData.comMod === "Sin asignar" ? (
                <Button
                  size="small"
                  onClick={() => {
                    setComModDrawerOpen(true);
                  }}
                  variant="outlined"
                  endIcon={<Edit />}
                >
                  EDITAR
                </Button>
              ) : (
                typographyForText(productAditionalData.comMod)
              )}
            </Stack>
          </Grid>
        )}
        <Grid item xs={12}>
          <Divider flexItem />
        </Grid>
        {/*Conditional rendering for POWER KW */}
        {productAditionalData.powerKw && (
          <Grid item xs={6}>
            <Stack
              alignItems="flex-start"
              justifyContent="flex-start"
              textAlign="left"
            >
              {typographyForCustomizedFontSize(
                "Potencia",
                "11px",
                null,
                "bold"
              )}
              {typographyForText(`${productAditionalData.powerKw}kW`, "bold")}
            </Stack>
          </Grid>
        )}
        {/**Conditional rendering for CONNECTOR TYPE */}
        {productAditionalData.connectorType && (
          <Grid item xs={6}>
            <Stack
              alignItems="flex-start"
              justifyContent="flex-start"
              textAlign="left"
            >
              {typographyForCustomizedFontSize(
                "Tipo de conector de salida",
                "11px",
                null,
                "bold"
              )}
              {typographyForText(productAditionalData.connectorType, "bold")}
            </Stack>
          </Grid>
        )}
        {/**Conditional rendering for POWER CONFIG TYPE */}
        {productAditionalData.powerConfigType && (
          <Grid item xs={6}>
            <Stack
              alignItems="flex-start"
              justifyContent="flex-start"
              textAlign="left"
            >
              {typographyForCustomizedFontSize(
                "Tipo de configuración",
                "11px",
                null,
                "bold"
              )}
              {typographyForText(productAditionalData.powerConfigType, "bold")}
            </Stack>
          </Grid>
        )}
        {/**Conditional rendering for RFID CARDS QUANTITY */}
        {productAditionalData.rfidCardsQuantity > 0 && (
          <Grid item xs={6}>
            <Stack
              alignItems="flex-start"
              justifyContent="flex-start"
              textAlign="left"
            >
              {typographyForCustomizedFontSize(
                "Cantidad de tarjetas RFID",
                "11px",
                null,
                "bold"
              )}
              {typographyForText(
                productAditionalData.rfidCardsQuantity,
                "bold"
              )}
            </Stack>
          </Grid>
        )}
        {/**Conditional rendering for TSJ CABLE LENGTH */}
        {productAditionalData.tsjCableLengthInMeters && (
          <Grid item xs={6}>
            <Stack
              alignItems="flex-start"
              justifyContent="flex-start"
              textAlign="left"
            >
              {typographyForCustomizedFontSize(
                "Longitud del cable TSJ",
                "11px",
                null,
                "bold"
              )}
              {typographyForText(
                `${productAditionalData.tsjCableLengthInMeters} metros`,
                "bold"
              )}
            </Stack>
          </Grid>
        )}
        {/**Conditional rendering for CONNECTOR CABLE LENGTH */}
        {productAditionalData.connectorCableLengthInMeters && (
          <Grid item xs={6}>
            <Stack
              alignItems="flex-start"
              justifyContent="flex-start"
              textAlign="left"
            >
              {typographyForCustomizedFontSize(
                "Longitud del cable del vehículo",
                "11px",
                null,
                "bold"
              )}
              {typographyForText(
                `${productAditionalData.connectorCableLengthInMeters} metros`,
                "bold"
              )}
            </Stack>
          </Grid>
        )}
        {/**Conditional rendering for TSJ INTERFACE */}
        {productAditionalData.tsjInterface && (
          <Grid item xs={6}>
            <Stack
              alignItems="flex-start"
              justifyContent="flex-start"
              textAlign="left"
            >
              {typographyForCustomizedFontSize(
                "Tipo de salida del cable TSJ",
                "11px",
                null,
                "bold"
              )}
              {typographyForText(productAditionalData.tsjInterface, "bold")}
            </Stack>
          </Grid>
        )}
        {/**Conditional rendering for POWER CORD CONNECTOR */}
        {productAditionalData.powerCordConnector && (
          <Grid item xs={6}>
            <Stack
              alignItems="flex-start"
              justifyContent="flex-start"
              textAlign="left"
            >
              {typographyForCustomizedFontSize(
                "Tipo de conector de entrada",
                "11px",
                null,
                "bold"
              )}
              {typographyForText(
                productAditionalData.powerCordConnector,
                "bold"
              )}
            </Stack>
          </Grid>
        )}
        {/**Conditional rendering for DATACARD */}
        {productAditionalData.datacard && (
          <Grid item xs={6}>
            <Stack
              alignItems="flex-start"
              justifyContent="flex-start"
              textAlign="left"
            >
              {typographyForCustomizedFontSize(
                "Datacard",
                "11px",
                null,
                "bold"
              )}
              {typographyForText(productAditionalData.datacard, "bold")}
            </Stack>
          </Grid>
        )}
        {/**Conditional rendering for ETHERNET */}
        {productAditionalData.ethernet && (
          <Grid item xs={6}>
            <Stack
              alignItems="flex-start"
              justifyContent="flex-start"
              textAlign="left"
            >
              {typographyForCustomizedFontSize(
                "Puerto RJ45",
                "11px",
                null,
                "bold"
              )}
              {typographyForText(productAditionalData.ethernet, "bold")}
            </Stack>
          </Grid>
        )}
        {/**Conditional rendering for WALLKIT */}
        {productAditionalData.wallkit && (
          <Grid item xs={6}>
            <Stack
              alignItems="flex-start"
              justifyContent="flex-start"
              textAlign="left"
            >
              {typographyForCustomizedFontSize(
                "Kit de Pared",
                "11px",
                null,
                "bold"
              )}
              {typographyForText(productAditionalData.wallkit, "bold")}
            </Stack>
          </Grid>
        )}
        {/**Conditional rendering for AGENCY */}
        {productAditionalData.company !== "" && (
          <Grid item xs={6}>
            <Stack
              alignItems="flex-start"
              justifyContent="flex-start"
              textAlign="left"
            >
              {typographyForCustomizedFontSize(
                "Agencia que solicita",
                "11px",
                null,
                "bold"
              )}
              {typographyForText(productAditionalData.company, "bold")}
            </Stack>
          </Grid>
        )}
        <Grid item xs={6}>
          <Stack
            alignItems="flex-start"
            justifyContent="flex-start"
            textAlign="left"
          >
            {typographyForCustomizedFontSize(
              "Otras especificaciones",
              "11px",
              null,
              "bold"
            )}
            {typographyForText(
              specifications === ""
                ? "No hay información sobre especificaciones para este producto"
                : specifications,
              "bold"
            )}
          </Stack>
        </Grid>
        <Grid item xs={6}>
          <Stack
            alignItems="flex-start"
            justifyContent="flex-start"
            textAlign="left"
          >
            {typographyForCustomizedFontSize(
              "Anotaciones sobre el producto",
              "11px",
              null,
              "bold"
            )}
            {typographyForText(
              annotations === ""
                ? "No hay anotaciones sobre este producto"
                : annotations,
              "bold"
            )}
          </Stack>
        </Grid>

        {userData.role === "ADMINISTRATOR" && (
          <Grid item xs={12} sx={{ marginTop: "10px", marginBottom: "10px" }}>
            <ContainedButton
              text={"Editar especificaciones"}
              endIcon={<Edit />}
              onClick={() => {
                setOpenSpecsDrawer(true);
              }}
            />
          </Grid>
        )}
      </Grid>
    </Card>
  );
}
